<script setup lang="ts">
import type { SignupOffer } from '@promotion/types'
import type { UIFooterProps, UIFooterNavColumn } from '@core/types'
import beGambleAwareSvg from '@/assets/authority/begambleaware-simple.svg?component'
import truelayerUrl from '@/assets/payment/truelayer_stack.svg?url'
import visaUrl from '@/assets/payment/visa.svg?url'
import mastercardUrl from '@/assets/payment/mastercard.svg?url'
import applePayDarkUrl from '@/assets/payment/applepay-dark.svg?url'
import applePayWhiteUrl from '@/assets/payment/applepay-white.svg?url'
import paypalUrl from '@/assets/payment/paypal.svg?url'
import eighteenPlusUrl from '@/assets/authority/eighteen-plus.svg?url'
import beGamblingAwareDarkUrl from '@/assets/authority/begambleaware.svg?url'
import beGamblingAwareWhiteUrl from '@/assets/authority/begambleaware-white.svg?url'
import visaWhiteUrl from '@/assets/payment/visa-white.svg?url'
import paypalWhiteUrl from '@/assets/payment/paypal-white.svg?url'
import eighteenPlusLightUrl from '@/assets/authority/eighteen-plus-light.svg?url'

export interface FooterContainerProps {
    authority?: UIFooterProps['authority']
    payment?: UIFooterProps['payment']
    navigation?: UIFooterNavColumn[]
    enableTerms?: boolean
    isLandingPage?: boolean
    license?: {
        text: string
        link: {
            text: string
            href: string
        }
    }
}

const _props = defineProps<FooterContainerProps>()
const props = useDefaults({
    props: _props,
    defaults: {
        enableTerms: true,
        isLandingPage: false,
        payment: [
            {
                src: truelayerUrl,
                width: 50,
                height: 35,
            },
            {
                src: visaUrl,
                width: 50,
                height: 30,
            },
            {
                src: mastercardUrl,
                width: 38,
                height: 30,
            },
            {
                src: applePayDarkUrl,
                width: 48,
                height: 30,
            },
            {
                src: paypalUrl,
                width: 78,
                height: 24,
            },
        ],
        authority: {
            items: [
                {
                    href: 'https://www.gamstop.co.uk/',
                    target: '_blank',
                    image: {
                        src: '/media/authority/gamstop.png',
                        height: 48,
                        width: 130,
                        alt: 'Gamstop'
                    },
                },
                {
                    href: 'https://www.gamblingcommission.gov.uk/',
                    target: '_blank',
                    image: {
                        src: '/media/authority/gambling-authority.png',
                        height: 35,
                        width: 118,
                        alt: 'Gambling commission'
                    },
                },
                {
                    href: '/safer-gambling',
                    image: {
                        src: eighteenPlusUrl,
                        height: 50,
                        width: 50,
                        alt: 'Eighteen plus'
                    },
                },
                {
                    href: 'https://www.begambleaware.org/',
                    image: {
                        src: beGamblingAwareDarkUrl,
                        height: 25,
                        width: 120,
                        alt: 'Begambleaware'
                    },
                },
            ]
        },
    }
})

const route = useRoute()
const { isExisting, isLoggedIn } = useUserInfo()
const { isVisible, resolveHref } = useNavigationFilter()
const { signupBundles } = useSignupBundles()
const { site, app: appConfig } = useCoreConfig()
const { isMobile } = useDevice()
const { isMobileApp } = useWebauthn()
const { theme } = useTheme()
const footerLogo = useThemeLogo('footer')
const socialLinks = useSiteSocialLinks()

const themeAssets: Record<string, string> = {
    [visaUrl]: visaWhiteUrl,
    [applePayDarkUrl]: applePayWhiteUrl,
    [paypalUrl]: paypalWhiteUrl,
    [eighteenPlusUrl]: eighteenPlusLightUrl,
    [beGamblingAwareDarkUrl]: beGamblingAwareWhiteUrl,
    '/media/authority/gamstop.png': '/media/authority/gamestop-on-dark.png',
}

function resolveThemeSrc(src: string): string {
    if (theme.value.dark) {
        return themeAssets[src] ?? src
    }
    return Object.keys(themeAssets).find(dark => themeAssets[dark] === src) ?? src
}

const authorityItems = computed(() => {
    return props.authority?.items?.map(item => ({
        ...item,
        image: { ...item.image, src: resolveThemeSrc(item.image.src) },
    }))
})

const paymentItems = computed(() => {
    return props.payment?.map(item => ({
        ...item,
        src: resolveThemeSrc(item.src),
    }))
})

function processColumn(column: UIFooterNavColumn) {
    return {
        ...column,
        items: column.items
            .filter(isVisible)
            .map(item => ({
                ...item,
                href: resolveHref(item),
            })),
    }
}

const footerNavigation = computed(() => (props.navigation ?? []).map(processColumn))

const terms = computed(() => {
    if (!signupBundles.value?.length) return []

    return signupBundles.value.map((bundle: SignupOffer) => {
        return {
            label: bundle.label,
            title: bundle.title,
            text: bundle.longTerms
        }
    })
})
const showPromotionTerms = computed(() => {
    if (isLoggedIn.value) return false

    return !route.meta.hidePromotions
        && route.meta?.termsShown
        && route?.query?.partner_id
})

const gambleAwareLinkString = '<a href="https://www.gambleaware.org" target="_blank" rel="noopener">www.gambleaware.org</a>'
</script>

<template>
    <UIFooter
        :navigation="footerNavigation"
        :payment="paymentItems"
        :social="socialLinks"
        :authority="{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            responsibleText: `
            We encourage safer gambling<br>
            Underage gambling is an offence<br>
            For more information and advice visit ${gambleAwareLinkString}`,
            ...props.authority,
            items: authorityItems
        }"
    >
        <template #brand>
            <NuxtLink :href="isExisting ? '/lobby' : '/'" aria-label="Back to frontpage">
                <NuxtImg
                    v-if="footerLogo"
                    v-bind="footerLogo"
                    preset="local"
                    provider="cloudflare"
                    decoding="async"
                    :alt="`${site.title} logo`"
                    loading="lazy"
                />
            </NuxtLink>
            <div v-if="isMobile && !isMobileApp" class="footer__app-store-links">
                <a
                    v-if="appConfig?.android"
                    :href="appConfig.android.link"
                    target="_blank"
                >
                    <NuxtImg
                        preset="local"
                        provider="cloudflare"
                        loading="lazy"
                        decoding="async"
                        :src="appConfig.android.image"
                        :alt="`Download ${site.title} in Google Play Store`"
                        height="40"
                        width="120"
                    />
                </a>

                <a
                    v-if="appConfig?.ios"
                    :href="appConfig.ios.link"
                    target="_blank"
                >
                    <NuxtImg
                        preset="local"
                        provider="cloudflare"
                        loading="lazy"
                        decoding="async"
                        :src="appConfig.ios.image"
                        :alt="`Download ${site.title} in App Store`"
                        height="40"
                        width="135"
                    />
                </a>
            </div>
        </template>

        <template #extras>
            <div v-if="enableTerms && !isExisting" class="terms">
                <ClientOnly v-if="showPromotionTerms">
                    <LazyPromotionTerms
                        v-if="terms?.length"
                        :terms="terms"
                        type="long"
                        subtle
                    />
                </ClientOnly>
            </div>

            <div class="compliance">
                <ComplianceLinks>
                    <template #top>
                        <div v-if="props.license" class="license-text">
                            {{ props.license.text }}
                            <NuxtLink
                                :href="props.license.link.href"
                                target="_blank"
                                external
                            >
                                {{ props.license.link.text }}
                            </NuxtLink>
                        </div>
                    </template>

                    <template #prepend>
                        <NuxtLink
                            class="icon begambleaware"
                            style="width: 106px; height: 12px; padding-bottom: 2px;"
                            href="https://www.begambleaware.org/"
                            target="_blank"
                            external
                        >
                            <component :is="beGambleAwareSvg" />
                        </NuxtLink>
                    </template>
                    <template #bottom>
                        <div class="parental-text">
                            Parental responsibility: Use device parental controls to prevent children accessing gambling.
                        </div>
                    </template>
                </ComplianceLinks>
            </div>
        </template>
    </UIFooter>
</template>

<style lang="scss" scoped>
.footer {
    :deep(a) {
        color: var(--link);

        &:hover,
        &:focus {
            color: var(--on-link-hover);
        }
    }

    .footer-text--small {
        opacity: 0.7;
        font-size: 10px;
        line-height: 12px;
        margin-block-end: 6px;
        margin-block-start: 6px;
        margin-top: 0px;
    }

    :deep(.offer-terms) {
        p:last-child:first-child {
            text-align: center;
        }
    }

    .license-text {
        font-size: 12px;

        a,
        a:hover {
            color: var(--on-background, #{$footer-font-color});
            text-decoration: underline;
        }
    }

    .parental-text {
        @extend .footer-text--small;

        margin-top: 20px;
    }

    :deep(.footer__authority) {
        ul.list-none {
            margin-bottom: 30px;
        }

        a {
            color: var(--on-link);
        }
    }

    :deep(h4) {
        font-family: $brand-font-family;
    }

    :deep(.footer__app-store-links) {
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-top: 16px;
    }
}

:deep(.compliance-links__wrapper) {
    *:nth-child(2) {
        border-left: unset;
    }
}

.begambleaware {
    line-height: 0;
    :deep(svg) {
        height: auto;
        width: auto;
        max-width: 100%;
        max-height: 100%;
        vertical-align: middle;

        path,
        rect,
        circle {
            fill: currentColor;
        }
    }

    @include tablet(md) {
        width: 106px;
    }
}
</style>
